/**
 * AuthPanelRedesign - Documentary Editorial aesthetic
 *
 * A warm, authentic authentication wrapper that matches the
 * WEattitude.org landing page redesign.
 *
 * Design: Split layout with editorial illustration panel + form
 */

import { memo, useEffect, useState } from "react";
import { Link } from "react-router-dom";

interface IAuthPanelRedesignProps {
  children: React.ReactNode;
  variant?: "signin" | "signup";
}

// Hand-drawn style decorative elements for the left panel
function EditorialIllustration({ variant }: { variant: "signin" | "signup" }) {
  return (
    <div className="relative w-full h-full overflow-hidden">
      {/* Base warm gradient */}
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(165deg, #C2410C 0%, #A16207 40%, #4D7C0F 100%)`,
        }}
      />

      {/* Paper texture overlay */}
      <div
        className="absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Large organic shape - community circle */}
      <svg
        className="absolute -left-20 top-1/4 w-[500px] h-[500px] opacity-20"
        viewBox="0 0 400 400"
      >
        <circle
          cx="200" cy="200" r="150"
          fill="none"
          stroke="#FFFEF9"
          strokeWidth="2"
          strokeDasharray="8 12"
        />
        <circle
          cx="200" cy="200" r="120"
          fill="none"
          stroke="#FFFEF9"
          strokeWidth="1.5"
        />
        <circle
          cx="200" cy="200" r="80"
          fill="#FFFEF9"
          fillOpacity="0.1"
        />
      </svg>

      {/* Hand-drawn connecting lines */}
      <svg
        className="absolute right-10 bottom-1/3 w-[200px] h-[300px] opacity-30"
        viewBox="0 0 200 300"
      >
        <path
          d="M20,50 Q100,80 180,50 Q150,150 100,180 Q50,200 80,280"
          fill="none"
          stroke="#FFFEF9"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <circle cx="20" cy="50" r="6" fill="#FFFEF9" />
        <circle cx="180" cy="50" r="4" fill="#FFFEF9" />
        <circle cx="100" cy="180" r="8" fill="#FFFEF9" />
        <circle cx="80" cy="280" r="5" fill="#FFFEF9" />
      </svg>

      {/* Small decorative dots representing community members */}
      <div className="absolute top-20 right-20 flex flex-wrap gap-3 w-32 opacity-40">
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="rounded-full bg-white"
            style={{
              width: `${6 + Math.random() * 8}px`,
              height: `${6 + Math.random() * 8}px`,
              opacity: 0.4 + Math.random() * 0.6,
            }}
          />
        ))}
      </div>

      {/* Content overlay */}
      <div className="relative z-10 flex flex-col justify-center items-start h-full p-10 lg:p-12">
        <div className="max-w-[300px]">
          {/* Accent line */}
          <div className="w-12 h-0.5 bg-white/40 mb-8" />

          {variant === "signin" ? (
            <blockquote style={{ fontFamily: "'Fraunces', serif" }}>
              <p className="text-white/60 text-xl lg:text-2xl leading-snug">
                The world doesn't need more bystanders.
              </p>
              <p className="text-white text-3xl lg:text-4xl font-semibold mt-3 leading-tight">
                It needs you.
              </p>
            </blockquote>
          ) : (
            <blockquote style={{ fontFamily: "'Fraunces', serif" }}>
              <p className="text-white text-3xl lg:text-4xl font-semibold leading-tight">
                See it.
              </p>
              <p className="text-white/70 text-3xl lg:text-4xl font-light mt-1 leading-tight">
                Say it.
              </p>
              <p className="text-white text-3xl lg:text-4xl font-semibold mt-1 leading-tight" style={{ color: '#FCD34D' }}>
                Solve it.
              </p>
            </blockquote>
          )}

          {/* Bottom accent line */}
          <div className="w-8 h-0.5 bg-white/20 mt-8" />
        </div>
      </div>
    </div>
  );
}

function AuthPanelRedesign({
  children,
  variant = "signin",
}: IAuthPanelRedesignProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <>
      {/* Font imports */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,400;0,9..144,500;0,9..144,600;1,9..144,400&family=Source+Sans+3:wght@300;400;500;600&display=swap');
      `}</style>

      <div
        className="min-h-screen w-full flex flex-col px-4 py-8 lg:px-0 lg:py-0"
        style={{
          backgroundColor: '#FAF7F2',
          fontFamily: "'Source Sans 3', sans-serif",
        }}
      >
        {/* Subtle background texture */}
        <div
          className="fixed inset-0 pointer-events-none opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />

        {/* Top bar: logo + back link */}
        <div className="relative z-10 p-6 lg:px-12 lg:pt-8 flex items-center justify-between">
          <Link to="/">
            <span
              className="text-xl font-medium tracking-tight"
              style={{ color: '#1C1917', fontFamily: "'Source Sans 3', sans-serif" }}
            >
              WE
              <span className="italic" style={{ fontFamily: "'Fraunces', serif" }}>
                attitude
              </span>
              <span style={{ color: '#78716C', fontSize: '0.7em' }}>.org</span>
            </span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm group"
            style={{ color: '#78716C' }}
          >
            <svg
              className="w-4 h-4 transition-transform group-hover:-translate-x-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span className="transition-colors group-hover:text-[#1C1917]" style={{ fontFamily: "'Source Sans 3', sans-serif" }}>
              Back to home
            </span>
          </Link>
        </div>

        <div
          className={`
            relative w-full max-w-[1000px] mx-auto flex flex-col lg:flex-row
            lg:overflow-hidden rounded-2xl shadow-xl
            transition-all duration-700 ease-out lg:my-auto
            ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
          `}
          style={{
            boxShadow: '0 25px 50px -12px rgba(28, 25, 23, 0.15)',
          }}
        >
          {/* Mobile quote banner */}
          <div
            className="lg:hidden px-6 py-6 text-center"
            style={{
              background: 'linear-gradient(135deg, #C2410C 0%, #A16207 40%, #4D7C0F 100%)',
            }}
          >
            {variant === "signin" ? (
              <p style={{ fontFamily: "'Fraunces', serif" }}>
                <span className="text-white/60 text-base">The world doesn't need more bystanders. </span>
                <span className="text-white text-lg font-semibold">It needs you.</span>
              </p>
            ) : (
              <p className="text-lg font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>
                <span className="text-white">See it. </span>
                <span className="text-white/70 font-light">Say it. </span>
                <span style={{ color: '#FCD34D' }}>Solve it.</span>
              </p>
            )}
          </div>

          {/* Left Panel - Editorial Illustration */}
          <div className="hidden lg:block lg:w-[45%] min-h-[600px]">
            <EditorialIllustration variant={variant} />
          </div>

          {/* Right Panel - Form */}
          <div
            className="flex-1 flex flex-col justify-center bg-white px-6 py-10 sm:px-12 lg:px-14 lg:py-12"
            style={{ backgroundColor: '#FFFEF9' }}
          >

            {/* Form content */}
            <div className="w-full max-w-[360px]">
              {children}
            </div>

            {/* Trust indicators */}
            <div
              className="mt-10 pt-6 border-t flex items-center gap-4 text-xs"
              style={{
                borderColor: '#E7E5E4',
                color: '#A8A29E',
              }}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span style={{ fontFamily: "'Source Sans 3', sans-serif" }}>
                Your data is protected and never shared
              </span>
            </div>
          </div>
        </div>

      </div>
    </>
  );
}

export default memo(AuthPanelRedesign);
